import React, { useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import Button from '@mui/material/Button';
import { useNavigate } from 'react-router-dom';
import TextField from '@mui/material/TextField';
import { DataGrid, useGridApiRef } from '@mui/x-data-grid';

import { GBENSContext } from '../GBENSContainer';
import { amendShipment, cancelShipment, deleteShipment, duplicateShipment } from '../../../api/GBENSDec';
import LoadingSpinner from '../../../components/LoadingSpinner';

import dayjs from 'dayjs';

import ModalTemplate from './_ModalTemplate';

export const SubmitModal = ({ open, onClose, hasError }) => {
  const { allValid } = useContext(GBENSContext);
  const navigate = useNavigate();

  return allValid ? (
    <ModalTemplate
      open={open}
      title={hasError ? 'Error Submitting Shipment' : 'Submitting Shipment'}
      text={
        hasError ? (
          <div>
            <br />
            <strong>Unable to submit due to connectivity issues.</strong>
            <br />
            The shipment will be automatically resubmitted once the connection is restored, and the shipment status will
            be updated accordingly. Please check back later.
          </div>
        ) : (
          'Your VALID declaration(s) have been submitted.'
        )
      }
      body={
        !hasError && (
          <div>
            <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between' }}>
              <div>Date:</div>
              <div>{dayjs().format('DD/MM/YY')}</div>
            </div>
            <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between' }}>
              <div>Time:</div>
              <div>{dayjs().format('HH:mm')}</div>
            </div>
            <div>Please check all your consignments have been accepted and received an MRN</div>
          </div>
        )
      }
      buttons={[
        <div key={0} />,
        <Button key={1} onClick={() => navigate('/Dashboard')}>
          Close
        </Button>,
      ]}
    />
  ) : (
    <ModalTemplate
      open={open}
      onClose={onClose}
      title="Error"
      text="There was an error with your declaration(s). Please review and try again."
      body={<div />}
      buttons={[
        <div key={0} />,
        <Button key={1} onClick={onClose}>
          Close
        </Button>,
      ]}
    />
  );
};
SubmitModal.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
};

export const DeleteConsignmentModal = ({ open, onClose }) => {
  const { deleteConsignmentRowsSelected, setDeleteConsignmentRowsSelected } = useContext(GBENSContext);
  const apiRef = useGridApiRef();

  const onSelect = () => {
    console.log(Object.values(Object.fromEntries(apiRef.current.getSelectedRows())).map((v) => v.id));
  };
  const columnsConsignments = [
    { field: 'reference', headerName: 'Reference', width: 180 },
    { field: 'consignor', headerName: 'Consignor', width: 180 },
    { field: 'consignee', headerName: 'Consignee', width: 180 },
  ];

  const rowsConsignments = [{ id: 1, reference: 'ABC', consignor: 'ABC LTD', consignee: 'DEF LTD' }];

  return (
    <ModalTemplate
      open={open}
      onClose={onClose}
      title="Delete a consignment"
      text="Please tick the consignments you would like to delete from this declaration."
      body={
        <DataGrid
          apiRef={apiRef}
          checkboxSelection
          onRowSelectionModelChange={(ids) => {
            setDeleteConsignmentRowsSelected(ids.length > 0);
          }}
          rows={rowsConsignments}
          columns={columnsConsignments}
        />
      }
      buttons={[
        <Button key={0} onClick={onClose}>
          Close
        </Button>,
        deleteConsignmentRowsSelected ? (
          <Button key={1} onClick={onSelect}>
            Delete
          </Button>
        ) : (
          <div />
        ),
      ]}
    />
  );
};

DeleteConsignmentModal.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
};

export const AmendShipmentModal = ({ open, onClose, headerId }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();

  const handleAmendShipment = async () => {
    try {
      setIsSubmitting(true);
      console.log('header id', headerId);
      await amendShipment(headerId);
      navigate('/Dashboard');
    } catch (e) {
      console.log(e);
    } finally {
      setIsSubmitting(false);
    }
    onClose();
  };
  return (
    <ModalTemplate
      open={open}
      onClose={onClose}
      title="Amend A Shipment"
      text={'Note: The MRN will change and the previously accepted MRN will be cancelled.'}
      buttons={[
        <Button key={0} onClick={onClose}>
          Close
        </Button>,
        <Button key={1} onClick={handleAmendShipment} className="duplicate-button" disabled={isSubmitting}>
          Amend
        </Button>,
      ]}
    />
  );
};

AmendShipmentModal.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  amendShipment: PropTypes.func,
};

export const DuplicateModal = ({ open, onClose, headerId }) => {
  const [jobReference, setJobReference] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    setJobReference('');
    setError(null);
    setIsSubmitting(false);
  }, [open]);

  const duplicate = async () => {
    const _error = !jobReference ? 'Please  enter a job reference' : null;
    if (_error) setError(_error);
    else {
      try {
        setIsSubmitting(true);
        await duplicateShipment(headerId, jobReference);
        navigate('/Dashboard');
      } catch (e) {
        console.log(e);
      } finally {
        setIsSubmitting(false);
      }
      onClose();
    }
  };

  const duplicateShipmentForm = (
    <div
      style={{
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        margin: 20,
      }}
    >
      <div style={{ width: '25%', fontWeight: 'bold', marginLeft: 30 }}>Job Reference</div>
      <div style={{ width: '70%', backgroundColor: 'white', padding: 15 }}>
        <TextField
          fullWidth
          value={jobReference}
          error={!!error}
          helperText={error || null}
          onChange={(e) => setJobReference(e.target.value)}
          variant="standard"
        />
      </div>
    </div>
  );

  const loadingScreen = (
    <>
      <div style={{ textAlign: 'center', paddingTop: 20 }}>Duplicating Shipment, please wait...</div>
      <LoadingSpinner />
    </>
  );

  return (
    <ModalTemplate
      open={open}
      onClose={onClose}
      title="Duplicate A Shipment"
      text={!isSubmitting && 'To duplicate a shipment and consignment details please enter a new job reference'}
      body={isSubmitting ? loadingScreen : duplicateShipmentForm}
      footerText={
        !isSubmitting && (
          <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', marginBottom: 0 }}>
            Please note, the Vehicle ID and Date/Time details will not be duplicated.
          </div>
        )
      }
      buttons={[
        <Button key={0} onClick={onClose}>
          Close
        </Button>,
        <Button key={1} onClick={duplicate} className="duplicate-button" disabled={isSubmitting}>
          Duplicate
        </Button>,
      ]}
    />
  );
};

DuplicateModal.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  headerId: PropTypes.string,
};

export const CancelShipmentModal = ({ open, onClose, headerId, reference, cancelOrDelete }) => {
  const navigate = useNavigate();
  const cancelShipmentCall = () => {
    try {
      if (cancelOrDelete === 'delete') {
        deleteShipment(headerId);
      } else {
        cancelShipment(headerId);
      }
      navigate('/Dashboard');
    } catch (e) {
      console.log(e);
    }
    onClose();
  };

  const bodyText =
    cancelOrDelete === 'delete'
      ? 'This action will delete the ENS declaration and all consignments associated with it. This action cannot be redone.'
      : "Cancelling a shipment will change the status to 'Cancelled' and remove the MRN. This action cannot be redone.";

  return (
    <ModalTemplate
      open={open}
      onClose={onClose}
      title={cancelOrDelete == 'delete' ? 'Delete a Shipment' : 'Cancel a Shipment'}
      body={<div className="delete-modal-text">{bodyText}</div>}
      buttons={[
        <Button className="cancel-button-delete" key={0} onClick={onClose}>
          Back
        </Button>,
        <Button key={1} className="delete-button" onClick={cancelShipmentCall}>
          {cancelOrDelete === 'delete' ? 'Delete Shipment' : 'Cancel Shipment'}
        </Button>,
      ]}
    />
  );
};

CancelShipmentModal.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  headerId: PropTypes.string,
  reference: PropTypes.string,
  cancelOrDelete: PropTypes.string,
};

export const CannotCancelShipmentModal = ({ open, onClose }) => {
  return (
    <ModalTemplate
      open={open}
      onClose={onClose}
      title="Unable to cancel this ENS / Consignment"
      text="Warning:"
      body={
        <div>
          <div>Items on this consignment have been submitted and this declaration is locked.</div>
          <div>Please try again when the responses have been received.</div>
        </div>
      }
      buttons={[
        <div key={0} />,
        <Button key={1} onClick={onClose}>
          Cancel
        </Button>,
      ]}
    />
  );
};

CannotCancelShipmentModal.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
};

export const SoftErrorModal = ({ open, onClose, message }) => {
  return (
    <ModalTemplate
      open={open}
      onClose={onClose}
      title="Issue submitting your ENS data"
      text={message}
      buttons={[
        <div key={0} />,
        <Button key={1} onClick={onClose}>
          Close
        </Button>,
      ]}
    />
  );
};

SoftErrorModal.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  message: PropTypes.string,
};
